import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Observable, Subscription, interval, of } from 'rxjs';
import { switchMap, takeWhile, finalize, catchError, tap } from 'rxjs/operators';
import { StatusId } from 'src/app/Models/organization.model';
import { ProductListing } from 'src/app/Models/productListing.model';
import { ProductListingService } from '../product-listing.service';
import { ActivatedRoute, Router } from '@angular/router';
import { NavService } from 'src/app/core/services/nav.service';
import { CheckStatus } from 'src/app/Models/checkStatus.model';

@Component({
  selector: 'app-product-listing-list',
  templateUrl: './product-listing-list.component.html',
  styleUrls: ['./product-listing-list.component.scss']
})
export class ProductListingListComponent implements OnInit, OnDestroy {
  loadingStatus: { [key: string]: boolean } = {};
  tableProductListings$: Observable<ProductListing[] | null>;
  StatusId = StatusId;
  private subscriptions = new Subscription();
  
  constructor(
    public service: ProductListingService, 
    private router: Router, 
    private route: ActivatedRoute, 
    private navServices: NavService,
    private cdr: ChangeDetectorRef
  ) {
    this.tableProductListings$ = this.service.productListings$;
  }
  
  toggleButton(productListing: ProductListing) {
    // Implement as needed
  }
  
  private navigateToProductListingForm(mode: 'create' | 'edit', productListing?: ProductListing) {
    this.service.setProductListingData(productListing);
    this.router.navigate([`./${mode}`], {
      relativeTo: this.route,
      queryParams: {}
    });
  }
  
  remove(id: number) {
    this.service.remove(id);
  }
  
  edit(productListing: ProductListing) {
    this.navigateToProductListingForm('edit', productListing);
  }
  
  downlodExcel(productListing: ProductListing) {
    // Set spinner state
    this.loadingStatus = { ...this.loadingStatus, [productListing.id]: true };
    this.cdr.detectChanges();
    
    // Create a more reactive approach using RxJS operators
    const sub = this.service.createReportFile(productListing.id).pipe(
      switchMap(() => {
        return interval(5000).pipe(
          switchMap(() => this.service.getProductListingStatus(productListing.id)),
          takeWhile((result: CheckStatus) => !result.isReady, true),
          switchMap((result: CheckStatus) => {
            if (result.isReady) {
              return this.downloadExcel(result.url, `${productListing.name}.xlsx`);
            }
            return of(null);
          })
        );
      }),
      finalize(() => {
        console.log('Download process completed or errored');
        this.loadingStatus = { ...this.loadingStatus, [productListing.id]: false };
        this.cdr.detectChanges();
      }),
      catchError((error) => {
        console.error('Error in download process:', error);
        this.handleDownloadError(error.error?.errorCode || 0, productListing.id);
        return of(null);
      })
    ).subscribe({
      next: (result) => {
        if (result) {
          console.log('Download successful');
          this.loadingStatus = { ...this.loadingStatus, [productListing.id]: false };
          this.cdr.detectChanges();
        }
      },
      error: (error) => {
        console.error('Unhandled error in subscription:', error);
        this.loadingStatus = { ...this.loadingStatus, [productListing.id]: false };
        this.cdr.detectChanges();
      }
    });
    
    this.subscriptions.add(sub);
  }
  
  handleDownloadError(errorCode: number, productListingId: number, intervalId?: number) {
    if (errorCode === 1000) {
      alert("Downloading error. Please try again.");
    } else {
      console.error('Unhandled error:', errorCode);
    }
    
    // Always reset loading status regardless of error
    this.loadingStatus = { ...this.loadingStatus, [productListingId]: false };
    this.cdr.detectChanges();
    
    if (intervalId !== undefined) {
      clearInterval(intervalId);
    }
  }
  
  downloadExcel(url: string, fileName: string): Observable<any> {
    return this.service.downloadExcel(url, fileName).pipe(
      tap(() => {
        console.log('Excel download complete');
      }),
      catchError((error) => {
        console.error('Error downloading Excel:', error);
        throw error;
      })
    );
  }
  
  createProductListing() {
    this.navigateToProductListingForm('create', new ProductListing());
  }
  
  ngOnInit() {
    this.navServices.collapseSidebar = true;
  }
  
  ngOnDestroy(): void {
    // Clean up all subscriptions to prevent memory leaks
    this.subscriptions.unsubscribe();
  }
}